import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  Select,
  Badge,
  useToast,
  Spinner,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardHeader,
  CardBody,
  Heading,
  IconButton,
  Flex,
  Tag,
  TagLabel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Code,
  Progress,
} from '@chakra-ui/react';
import {
  LinkIcon,
  DeleteIcon,
  RepeatIcon,
  CheckIcon,
  AttachmentIcon,
  AddIcon,
  CloseIcon,
} from '@chakra-ui/icons';

const API_HEADERS = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const DEFAULT_MANUAL_MAPPINGS = {
  register_infraction: {
    veiculoPlaca: 'plate',
    codigoInfracao: 'infraction_codes.0',
    dataInfracao: 'detected_at',
    camera: 'camera_name'
  },
  query_vehicle: {
    placa: 'plate'
  },
  query_driver: {
    cpf: 'cpf'
  },
  query_infraction: {
    numeroAuto: 'numeroAuto',
    codigoOrgao: 'codigoOrgao'
  },
  authenticate: {
    username: 'username',
    password: 'password'
  },
  other: {
    placa: 'plate'
  }
};

const DEFAULT_RESPONSE_MAPPINGS = {};

const createMappingRowId = () => `row-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const parseStaticMappingValue = (value) => {
  const trimmed = String(value ?? '').trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed !== '' && !Number.isNaN(Number(trimmed))) return Number(trimmed);
  return trimmed;
};

const formatStaticMappingValue = (value) => {
  if (typeof value === 'boolean') return String(value);
  if (value === null || value === undefined) return '';
  return String(value);
};

const fieldMappingsToRequestRows = (fieldMappings = {}) => {
  const rows = Object.entries(fieldMappings).map(([apiField, mapping]) => {
    if (typeof mapping === 'object' && mapping !== null && Object.prototype.hasOwnProperty.call(mapping, 'template')) {
      return {
        id: createMappingRowId(),
        apiField,
        sourceType: 'template',
        value: String(mapping.template ?? '')
      };
    }

    if (typeof mapping === 'object' && mapping !== null && Object.prototype.hasOwnProperty.call(mapping, 'static')) {
      return {
        id: createMappingRowId(),
        apiField,
        sourceType: 'static',
        value: formatStaticMappingValue(mapping.static)
      };
    }

    return {
      id: createMappingRowId(),
      apiField,
      sourceType: 'field',
      value: typeof mapping === 'object' && mapping !== null ? JSON.stringify(mapping) : String(mapping || '')
    };
  });

  return rows.length > 0
    ? rows
    : [{ id: createMappingRowId(), apiField: '', sourceType: 'field', value: '' }];
};

const responseMappingsToRows = (responseMappings = {}) => {
  const rows = Object.entries(responseMappings).map(([internalField, apiPath]) => ({
    id: createMappingRowId(),
    internalField,
    apiPath: String(apiPath || '')
  }));

  return rows.length > 0
    ? rows
    : [{ id: createMappingRowId(), internalField: '', apiPath: '' }];
};

const requestRowsToFieldMappings = (rows = []) => {
  return rows.reduce((acc, row) => {
    const apiField = String(row.apiField || '').trim();
    if (!apiField) return acc;

    if (row.sourceType === 'static') {
      acc[apiField] = { static: parseStaticMappingValue(row.value) };
      return acc;
    }

    if (row.sourceType === 'template') {
      acc[apiField] = { template: String(row.value || '') };
      return acc;
    }

    const internalField = String(row.value || '').trim();
    if (!internalField) return acc;
    if (internalField.startsWith('{') || internalField.startsWith('[')) {
      try {
        acc[apiField] = JSON.parse(internalField);
        return acc;
      } catch {
        acc[apiField] = internalField;
        return acc;
      }
    }
    acc[apiField] = internalField;

    return acc;
  }, {});
};

const responseRowsToMappings = (rows = []) => {
  return rows.reduce((acc, row) => {
    const internalField = String(row.internalField || '').trim();
    const apiPath = String(row.apiPath || '').trim();
    if (internalField && apiPath) {
      acc[internalField] = apiPath;
    }
    return acc;
  }, {});
};

const getInitialRequestRows = (purpose) => fieldMappingsToRequestRows(DEFAULT_MANUAL_MAPPINGS[purpose] || {});

const getInitialResponseRows = (purpose) => responseMappingsToRows(DEFAULT_RESPONSE_MAPPINGS[purpose] || {});

const DEFAULT_MANUAL_TEST_PAYLOADS = {
  register_infraction: {
    plate: 'ABC1D23',
    infraction_codes: ['7455'],
    detected_at: '2026-06-03T12:00:00Z',
    camera_name: 'Camera 01',
    image_url: 'https://exemplo.local/imagens/registro.jpg',
    vehicle_type: 'car'
  },
  query_vehicle: {
    plate: 'ABC1D23',
    vehicle_type: 'car'
  },
  query_driver: {
    cpf: '12345678901'
  },
  query_infraction: {
    numeroAuto: '000123456',
    codigoOrgao: '123456'
  },
  authenticate: {
    username: 'usuario_teste',
    password: 'senha_teste'
  },
  other: {}
};

const EMPTY_MANUAL_FORM = {
  name: '',
  description: '',
  base_url: '',
  auth_type: 'none',
  auth_token: '',
  auth_username: '',
  auth_password: '',
  auth_api_key: '',
  auth_api_header: 'X-API-Key',
  route_name: '',
  route_description: '',
  route_purpose: 'query_vehicle',
  route_method: 'POST',
  route_path: '/veiculos'
};

const PAGE_SIZE = 3;

const LOG_PURPOSE_LABELS = {
  register_infraction: 'Registrar infração',
  register_passage: 'Enviar passagem',
  query_vehicle: 'Consultar veículo',
  query_driver: 'Consultar condutor',
  query_infraction: 'Consultar infração',
  authenticate: 'Autenticação',
  other: 'Outro'
};

const IntegrationLogs = ({ integrationId }) => {
  const [logs, setLogs] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (offset, append) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const response = await fetch(
        `/api/integrations/${integrationId}/logs?limit=${PAGE_SIZE}&offset=${offset}`,
        { headers: API_HEADERS() }
      );
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Erro ao carregar logs');
      const next = Array.isArray(body.logs) ? body.logs : [];
      setLogs((current) => (append ? [...current, ...next] : next));
      setHasMore(Boolean(body.hasMore));
    } catch (error) {
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [integrationId]);

  useEffect(() => {
    load(0, false);
  }, [load]);

  return (
    <Box mt={3}>
      <Text fontSize="sm" fontWeight="medium" mb={2}>Últimos envios</Text>
      {loading ? (
        <Spinner size="sm" />
      ) : logs.length === 0 ? (
        <Text fontSize="xs" color="gray.500">Nenhum envio registrado ainda.</Text>
      ) : (
        <VStack align="stretch" spacing={2}>
          {logs.map((log) => (
            <HStack key={log.id} align="flex-start" spacing={3} p={2} bg="gray.50" borderRadius="md">
              <Badge colorScheme={log.success ? 'green' : 'red'} mt={0.5}>
                {log.success ? 'OK' : 'Erro'}
              </Badge>
              <Box flex="1">
                <Text fontSize="xs" color="gray.500">
                  {log.createdAt ? new Date(log.createdAt).toLocaleString('pt-BR') : ''}
                  {log.purpose ? ` · ${LOG_PURPOSE_LABELS[log.purpose] || log.purpose}` : ''}
                  {log.status_code ? ` · HTTP ${log.status_code}` : ''}
                </Text>
                <Text fontSize="sm">{log.message || (log.success ? 'Concluído' : 'Falha no envio')}</Text>
              </Box>
            </HStack>
          ))}
          {hasMore && (
            <Button
              size="xs"
              variant="link"
              colorScheme="teal"
              alignSelf="flex-start"
              isLoading={loadingMore}
              onClick={() => load(logs.length, true)}
            >
              Carregar mais
            </Button>
          )}
        </VStack>
      )}
    </Box>
  );
};

const ApiIntegrationTab = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [sourceType, setSourceType] = useState('text');
  const [documentationText, setDocumentationText] = useState('');
  const [documentationUrl, setDocumentationUrl] = useState('');
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [processJob, setProcessJob] = useState(null);
  const [manualSchema, setManualSchema] = useState(null);
  const [manualForm, setManualForm] = useState(EMPTY_MANUAL_FORM);
  const [manualRequestRows, setManualRequestRows] = useState(getInitialRequestRows('query_vehicle'));
  const [manualResponseRows, setManualResponseRows] = useState(getInitialResponseRows('query_vehicle'));
  const [manualTestPayloadText, setManualTestPayloadText] = useState(JSON.stringify(DEFAULT_MANUAL_TEST_PAYLOADS.query_vehicle, null, 2));
  const [savingManual, setSavingManual] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingRouteIndex, setEditingRouteIndex] = useState(0);
  const [editingRoutes, setEditingRoutes] = useState([]);
  const [editingAuthType, setEditingAuthType] = useState('none');
  const manualCardRef = useRef(null);
  const [testingManual, setTestingManual] = useState(false);
  const [manualTestResult, setManualTestResult] = useState(null);

  const toast = useToast();

  const fetchIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/integrations', { headers: API_HEADERS() });
      if (!response.ok) throw new Error('Erro ao carregar integrações');
      const data = await response.json();
      setIntegrations(data.integrations || []);
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  useEffect(() => {
    const fetchManualSchema = async () => {
      try {
        const response = await fetch('/api/integrations/manual/schema', { headers: API_HEADERS() });
        if (!response.ok) return;
        const data = await response.json();
        setManualSchema(data);
      } catch {
        setManualSchema(null);
      }
    };

    fetchManualSchema();
  }, []);

  const updateManualForm = (field, value) => {
    setManualForm((current) => ({ ...current, [field]: value }));
  };

  const handleManualPurposeChange = (purpose) => {
    if (editingId) {
      updateManualForm('route_purpose', purpose);
      return;
    }
    const defaultPaths = {
      register_infraction: '/infracoes',
      query_vehicle: '/veiculos',
      query_driver: '/condutores/{cpf}',
      query_infraction: '/infracoes/{numeroAuto}',
      authenticate: '/auth/token',
      other: '/'
    };
    const defaultMethods = {
      register_infraction: 'POST',
      query_vehicle: 'POST',
      query_driver: 'GET',
      query_infraction: 'GET',
      authenticate: 'POST',
      other: 'POST'
    };

    setManualForm((current) => ({
      ...current,
      route_purpose: purpose,
      route_method: defaultMethods[purpose] || 'GET',
      route_path: defaultPaths[purpose] || current.route_path
    }));
    setManualRequestRows(getInitialRequestRows(purpose));
    setManualResponseRows(getInitialResponseRows(purpose));
    setManualTestPayloadText(JSON.stringify(DEFAULT_MANUAL_TEST_PAYLOADS[purpose] || {}, null, 2));
    setManualTestResult(null);
  };

  const updateManualRequestRow = (rowId, field, value) => {
    setManualRequestRows((current) => current.map((row) => (
      row.id === rowId ? { ...row, [field]: value } : row
    )));
  };

  const addManualRequestRow = () => {
    setManualRequestRows((current) => [
      ...current,
      { id: createMappingRowId(), apiField: '', sourceType: 'field', value: '' }
    ]);
  };

  const removeManualRequestRow = (rowId) => {
    setManualRequestRows((current) => {
      const next = current.filter((row) => row.id !== rowId);
      return next.length > 0
        ? next
        : [{ id: createMappingRowId(), apiField: '', sourceType: 'field', value: '' }];
    });
  };

  const updateManualResponseRow = (rowId, field, value) => {
    setManualResponseRows((current) => current.map((row) => (
      row.id === rowId ? { ...row, [field]: value } : row
    )));
  };

  const addManualResponseRow = () => {
    setManualResponseRows((current) => [
      ...current,
      { id: createMappingRowId(), internalField: '', apiPath: '' }
    ]);
  };

  const removeManualResponseRow = (rowId) => {
    setManualResponseRows((current) => {
      const next = current.filter((row) => row.id !== rowId);
      return next.length > 0
        ? next
        : [{ id: createMappingRowId(), internalField: '', apiPath: '' }];
    });
  };

  const applySystemFieldToRequestRow = (fieldKey) => {
    setManualRequestRows((current) => {
      const emptyRowIndex = current.findIndex((row) => !String(row.apiField || '').trim());
      if (emptyRowIndex >= 0) {
        return current.map((row, index) => (
          index === emptyRowIndex
            ? { ...row, sourceType: 'field', value: fieldKey }
            : row
        ));
      }

      return [
        ...current,
        { id: createMappingRowId(), apiField: fieldKey, sourceType: 'field', value: fieldKey }
      ];
    });
  };

  const applyResponseSuggestionToRow = (fieldKey, apiPathExample) => {
    setManualResponseRows((current) => {
      const emptyRowIndex = current.findIndex((row) => !String(row.internalField || '').trim());
      if (emptyRowIndex >= 0) {
        return current.map((row, index) => (
          index === emptyRowIndex
            ? { ...row, internalField: fieldKey, apiPath: apiPathExample || row.apiPath }
            : row
        ));
      }

      return [
        ...current,
        { id: createMappingRowId(), internalField: fieldKey, apiPath: apiPathExample || '' }
      ];
    });
  };

  const buildApiFieldMappings = () => requestRowsToFieldMappings(manualRequestRows);

  const buildResponseMappings = () => responseRowsToMappings(manualResponseRows);

  const getManualAuthConfig = () => {
    if (manualForm.auth_type === 'bearer' || manualForm.auth_type === 'authorization') return { token: manualForm.auth_token };
    if (manualForm.auth_type === 'api_key') return { key: manualForm.auth_api_key, header: manualForm.auth_api_header || 'X-API-Key' };
    if (manualForm.auth_type === 'basic') return { username: manualForm.auth_username, password: manualForm.auth_password };
    return null;
  };

  const getPathParamMappings = (path, fieldMappings) => {
    const matches = String(path || '').matchAll(/\{([^}]+)\}/g);
    return Array.from(matches).reduce((params, match) => {
      const paramName = match[1];
      params[paramName] = fieldMappings[paramName] || paramName;
      return params;
    }, {});
  };

  const parseManualJson = (text, label) => {
    try {
      return text.trim() ? JSON.parse(text) : {};
    } catch {
      throw new Error(`${label} precisa ser um JSON válido.`);
    }
  };

  const buildManualRequestPayload = (fieldMappings, responseMappings) => {
    const pathParams = getPathParamMappings(manualForm.route_path, fieldMappings);
    const shouldUseQuery = manualForm.route_method === 'GET' && Object.keys(pathParams).length === 0;

    return {
      name: manualForm.name,
      description: manualForm.description,
      base_url: manualForm.base_url,
      auth_type: manualForm.auth_type,
      auth_config: getManualAuthConfig(),
      route: {
        name: manualForm.route_name,
        description: manualForm.route_description,
        purpose: manualForm.route_purpose,
        method: manualForm.route_method,
        path: manualForm.route_path,
        path_params: pathParams,
        query_params: shouldUseQuery ? fieldMappings : {},
        field_mappings: fieldMappings,
        response_mappings: responseMappings
      }
    };
  };

  const handleCreateManual = async () => {
    if (!manualForm.name || !manualForm.base_url || !manualForm.route_path) {
      toast({ title: 'Preencha nome, URL base e endpoint', status: 'warning', duration: 3000 });
      return;
    }

    const fieldMappings = buildApiFieldMappings();
    const responseMappings = buildResponseMappings();
    if (Object.keys(fieldMappings).length === 0) {
      toast({ title: 'Mapeamento obrigatório', description: 'Adicione ao menos um campo da API no corpo/query da requisição.', status: 'warning', duration: 4000 });
      return;
    }

    setSavingManual(true);
    try {
      const response = await fetch('/api/integrations/manual', {
        method: 'POST',
        headers: API_HEADERS(),
        body: JSON.stringify(buildManualRequestPayload(fieldMappings, responseMappings))
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Erro ao criar integração manual');

      toast({ title: 'Integração manual criada', description: data.integration?.name, status: 'success', duration: 4000 });
      setManualForm(EMPTY_MANUAL_FORM);
      setManualRequestRows(getInitialRequestRows('query_vehicle'));
      setManualResponseRows(getInitialResponseRows('query_vehicle'));
      setManualTestPayloadText(JSON.stringify(DEFAULT_MANUAL_TEST_PAYLOADS.query_vehicle, null, 2));
      setManualTestResult(null);
      fetchIntegrations();
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setSavingManual(false);
    }
  };

  const handleTestManualRoute = async () => {
    if (!manualForm.name || !manualForm.base_url || !manualForm.route_path) {
      toast({ title: 'Preencha nome, URL base e endpoint', status: 'warning', duration: 3000 });
      return;
    }

    let testInput;
    try {
      testInput = parseManualJson(manualTestPayloadText, 'Os dados de teste');
    } catch (error) {
      toast({ title: 'JSON inválido', description: error.message, status: 'error', duration: 4000 });
      return;
    }

    const fieldMappings = buildApiFieldMappings();
    const responseMappings = buildResponseMappings();
    if (Object.keys(fieldMappings).length === 0) {
      toast({ title: 'Mapeamento obrigatório', description: 'Adicione ao menos um campo da API no corpo/query da requisição.', status: 'warning', duration: 4000 });
      return;
    }

    setTestingManual(true);
    setManualTestResult(null);

    try {
      const response = await fetch('/api/integrations/manual/test', {
        method: 'POST',
        headers: API_HEADERS(),
        body: JSON.stringify({
          ...buildManualRequestPayload(fieldMappings, responseMappings),
          test_input: testInput
        })
      });
      const data = await response.json();

      if (!response.ok) {
        setManualTestResult({ success: false, ...data });
        toast({ title: 'Falha no teste', description: data.message || 'Erro ao testar rota manual', status: 'error', duration: 5000 });
        return;
      }

      setManualTestResult({ success: true, ...data });
      toast({ title: 'Teste executado', description: `Status HTTP ${data.response?.status || response.status}`, status: 'success', duration: 4000 });
    } catch (error) {
      setManualTestResult({ success: false, message: error.message });
      toast({ title: 'Falha no teste', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setTestingManual(false);
    }
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const pollProcessJob = async (jobId) => {
    while (true) {
      await wait(1000);

      const response = await fetch(`/api/integrations/process/jobs/${jobId}`, {
        headers: API_HEADERS()
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao acompanhar processamento');
      }

      setProcessJob(data.job);

      if (data.job.status === 'completed') {
        return data.job.result;
      }

      if (data.job.status === 'failed') {
        throw new Error(data.job.error?.message || 'Erro ao processar documentação');
      }
    }
  };

  const handleProcess = async () => {
    if (!geminiApiKey) {
      toast({ title: 'Erro', description: 'Insira a chave da API do Google Gemini', status: 'warning', duration: 3000 });
      return;
    }

    if (sourceType === 'text' && (!documentationText || documentationText.trim().length < 20)) {
      toast({ title: 'Erro', description: 'Insira a documentação da API (mínimo 20 caracteres)', status: 'warning', duration: 3000 });
      return;
    }

    if (sourceType === 'link' && !documentationUrl) {
      toast({ title: 'Erro', description: 'Insira a URL da documentação', status: 'warning', duration: 3000 });
      return;
    }

    if (sourceType === 'pdf' && !pdfFile) {
      toast({ title: 'Erro', description: 'Selecione um arquivo PDF', status: 'warning', duration: 3000 });
      return;
    }

    setProcessing(true);
    setProcessJob({ progress: 1, message: 'Processamento iniciado. Preparando documentação.', status: 'processing' });

    try {
      let response;

      if (sourceType === 'pdf') {
        const formData = new FormData();
        formData.append('source_type', 'pdf');
        formData.append('gemini_api_key', geminiApiKey);
        formData.append('pdf_file', pdfFile);

        response = await fetch('/api/integrations/process/jobs', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formData
        });
      } else {
        response = await fetch('/api/integrations/process/jobs', {
          method: 'POST',
          headers: API_HEADERS(),
          body: JSON.stringify({
            source_type: sourceType,
            documentation_text: sourceType === 'text' ? documentationText : undefined,
            documentation_url: sourceType === 'link' ? documentationUrl : undefined,
            gemini_api_key: geminiApiKey
          })
        });
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao processar documentação');
      }

      setProcessJob(data.job);
      const result = await pollProcessJob(data.job_id);
      const integration = result.integration;

      toast({
        title: 'Integração criada!',
        description: `${integration.name} - ${integration.routes?.routes?.length || 0} rotas identificadas`,
        status: 'success',
        duration: 5000
      });

      // Limpar formulário
      setDocumentationText('');
      setDocumentationUrl('');
      setPdfFile(null);

      fetchIntegrations();
    } catch (error) {
      setProcessJob((current) => current ? { ...current, status: 'failed', message: error.message, progress: 100 } : null);
      toast({ title: 'Erro ao processar', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setProcessing(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await fetch(`/api/integrations/${id}/toggle`, {
        method: 'PUT',
        headers: API_HEADERS()
      });
      if (!response.ok) throw new Error('Erro ao alterar status');
      fetchIntegrations();
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000 });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta integração?')) return;

    try {
      const response = await fetch(`/api/integrations/${id}`, {
        method: 'DELETE',
        headers: API_HEADERS()
      });
      if (!response.ok) throw new Error('Erro ao excluir');
      toast({ title: 'Integração excluída', status: 'success', duration: 3000 });
      fetchIntegrations();
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000 });
    }
  };

  const handleReprocess = async (id) => {
    try {
      const response = await fetch(`/api/integrations/${id}/reprocess`, {
        method: 'POST',
        headers: API_HEADERS()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      toast({ title: 'Reprocessado com sucesso', status: 'success', duration: 3000 });
      fetchIntegrations();
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 3000 });
    }
  };

  const openEdit = (integration) => {
    const documentRoutes = parseRouteDocument(integration.routes);
    const routes = Array.isArray(documentRoutes.routes) ? documentRoutes.routes : [];
    const route = routes[0] || {};
    setEditingId(integration.id);
    setEditingAuthType(integration.auth_type || 'none');
    setEditingRoutes(routes);
    setEditingRouteIndex(0);
    setManualForm({
      ...EMPTY_MANUAL_FORM,
      name: integration.name || '',
      description: integration.description || '',
      base_url: integration.base_url || '',
      auth_type: integration.auth_type || 'none',
      route_name: route.name || '',
      route_description: route.description || '',
      route_purpose: route.purpose || 'other',
      route_method: route.method || 'POST',
      route_path: route.path || route.full_url || ''
    });
    setManualRequestRows(fieldMappingsToRequestRows(route.field_mappings || {}));
    setManualResponseRows(responseMappingsToRows(route.response_mappings || {}));
    setManualTestResult(null);
    setTimeout(() => manualCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  const loadEditingRoute = (index) => {
    const route = editingRoutes[index] || {};
    setEditingRouteIndex(index);
    setManualForm((current) => ({
      ...current,
      auth_token: '',
      auth_password: '',
      auth_api_key: '',
      route_name: route.name || '',
      route_description: route.description || '',
      route_purpose: route.purpose || 'other',
      route_method: route.method || 'POST',
      route_path: route.path || route.full_url || ''
    }));
    setManualRequestRows(fieldMappingsToRequestRows(route.field_mappings || {}));
    setManualResponseRows(responseMappingsToRows(route.response_mappings || {}));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingRoutes([]);
    setEditingRouteIndex(0);
    setManualForm(EMPTY_MANUAL_FORM);
    setManualRequestRows(getInitialRequestRows('query_vehicle'));
    setManualResponseRows(getInitialResponseRows('query_vehicle'));
    setManualTestResult(null);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    if (!manualForm.name || !manualForm.base_url || !manualForm.route_path) {
      toast({ title: 'Preencha nome, URL base e endpoint', status: 'warning', duration: 3000 });
      return;
    }
    const fieldMappings = buildApiFieldMappings();
    const responseMappings = buildResponseMappings();
    if (Object.keys(fieldMappings).length === 0) {
      toast({ title: 'Mapeamento obrigatório', description: 'Adicione ao menos um campo da API.', status: 'warning', duration: 4000 });
      return;
    }
    const secretsFilled = Boolean(manualForm.auth_token || manualForm.auth_password || manualForm.auth_api_key);
    const keepAuth = !secretsFilled && manualForm.auth_type === editingAuthType;
    if (!keepAuth && manualForm.auth_type !== 'none' && !secretsFilled) {
      toast({ title: 'Informe o segredo da autenticação', status: 'warning', duration: 4000 });
      return;
    }

    setSavingManual(true);
    try {
      const response = await fetch(`/api/integrations/${editingId}/manual`, {
        method: 'PUT',
        headers: API_HEADERS(),
        body: JSON.stringify({
          ...buildManualRequestPayload(fieldMappings, responseMappings),
          route_index: editingRouteIndex,
          keep_auth: keepAuth
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Erro ao atualizar integração');
      toast({ title: 'Integração atualizada', status: 'success', duration: 3000 });
      cancelEdit();
      fetchIntegrations();
    } catch (error) {
      toast({ title: 'Erro', description: error.message, status: 'error', duration: 4000 });
    } finally {
      setSavingManual(false);
    }
  };

  const parseRoutes = (routesStr) => {
    try {
      const parsed = typeof routesStr === 'string' ? JSON.parse(routesStr) : routesStr;
      return parsed.routes || parsed || [];
    } catch {
      return [];
    }
  };

  const parseRouteDocument = (routesStr) => {
    try {
      const parsed = typeof routesStr === 'string' ? JSON.parse(routesStr) : routesStr;
      return Array.isArray(parsed) ? { routes: parsed } : (parsed || { routes: [] });
    } catch {
      return { routes: [] };
    }
  };

  const isManualIntegration = (integration) => parseRouteDocument(integration.routes).source === 'manual';

  const getSourceLabel = (integration) => {
    if (isManualIntegration(integration)) return 'MANUAL';
    return integration.source_type?.toUpperCase() || 'API';
  };

  const getMethodColor = (method) => {
    const colors = { GET: 'green', POST: 'blue', PUT: 'orange', DELETE: 'red', PATCH: 'purple' };
    return colors[method?.toUpperCase()] || 'gray';
  };

  const getPurposeLabel = (purpose) => {
    const labels = {
      register_infraction: '📝 Registrar Infração',
      register_passage: '🚗 Enviar Passagem',
      query_infraction: '🔍 Consultar Infração',
      query_vehicle: '🚗 Consultar Veículo',
      query_driver: '🪪 Consultar Condutor',
      register_vehicle: '🚙 Registrar Veículo',
      authenticate: '🔐 Autenticação',
      other: '📋 Outro'
    };
    return labels[purpose] || purpose;
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Formulário de nova integração */}
      <Card>
        <CardHeader>
          <Heading size="md">API de Infrações</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Forneça a documentação da API externa e a IA fará a integração automaticamente
          </Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Como funciona?</AlertTitle>
                <AlertDescription fontSize="xs">
                  1. Forneça a documentação da API via <strong>Link</strong>, <strong>PDF</strong> ou <strong>Texto colado</strong><br />
                  2. A IA (Google Gemini) analisará e extrairá as rotas automaticamente<br />
                  3. As rotas ficarão salvas no banco de dados<br />
                  4. Na tela de Batches, ao confirmar infrações, você poderá enviar para a API integrada
                </AlertDescription>
              </Box>
            </Alert>

            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Revise antes de ativar</AlertTitle>
                <AlertDescription fontSize="xs">
                  A IA pode interpretar algum endpoint, campo ou regra de forma incorreta. Faça uma revisão manual das rotas, autenticação e mapeamentos antes de usar a integração em produção.
                </AlertDescription>
              </Box>
            </Alert>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Chave da API do Google Gemini</FormLabel>
              <Input
                type="password"
                placeholder="Insira sua API Key do Google Gemini"
                value={geminiApiKey}
                onChange={(e) => setGeminiApiKey(e.target.value)}
                size="sm"
              />
              <FormHelperText fontSize="xs">
                Obtenha em: <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" style={{ color: '#3182CE' }}>aistudio.google.com/apikey</a>
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Tipo de documentação</FormLabel>
              <Select value={sourceType} onChange={(e) => setSourceType(e.target.value)} size="sm">
                <option value="text">Texto (colar documentação)</option>
                <option value="link">Link (URL da documentação)</option>
                <option value="pdf">PDF (upload de arquivo)</option>
              </Select>
              <FormHelperText fontSize="xs" color="orange.500">
                Apenas estes 3 formatos são suportados: Link, PDF ou Texto colado
              </FormHelperText>
            </FormControl>

            {sourceType === 'text' && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Documentação da API</FormLabel>
                <Textarea
                  placeholder={"Cole aqui toda a documentação da API...\n\nExemplo:\nPOST /api/v1/infractions\nBody: { plate: string, code: number, date: string }\nResponse: { id: number, status: string }\n\nGET /api/v1/vehicles/{plate}\nResponse: { plate: string, owner: string, model: string }"}
                  value={documentationText}
                  onChange={(e) => setDocumentationText(e.target.value)}
                  minH="200px"
                  size="sm"
                  fontFamily="mono"
                  fontSize="xs"
                />
                <FormHelperText fontSize="xs">
                  Inclua: endpoints, métodos HTTP, parâmetros, exemplos de request/response, autenticação
                </FormHelperText>
              </FormControl>
            )}

            {sourceType === 'link' && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">URL da Documentação</FormLabel>
                <InputGroup size="sm">
                  <InputLeftElement>
                    <LinkIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="https://api.exemplo.com/docs ou swagger.json"
                    value={documentationUrl}
                    onChange={(e) => setDocumentationUrl(e.target.value)}
                  />
                </InputGroup>
                <FormHelperText fontSize="xs">
                  Suporta páginas HTML, JSON (Swagger/OpenAPI), documentação em texto
                </FormHelperText>
              </FormControl>
            )}

            {sourceType === 'pdf' && (
              <FormControl isRequired>
                <FormLabel fontSize="sm">Arquivo PDF</FormLabel>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setPdfFile(e.target.files[0])}
                  size="sm"
                  p={1}
                />
                {pdfFile && (
                  <Tag mt={2} colorScheme="blue" size="sm">
                    <AttachmentIcon mr={1} />
                    <TagLabel>{pdfFile.name}</TagLabel>
                  </Tag>
                )}
              </FormControl>
            )}

            <Button
              colorScheme="teal"
              onClick={handleProcess}
              isLoading={processing}
              loadingText="Processando com IA..."
              leftIcon={<CheckIcon />}
              isDisabled={!geminiApiKey}
            >
              Processar Documentação e Criar Integração
            </Button>

            {processJob && (processing || processJob.status === 'failed') && (
              <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                <HStack justify="space-between" mb={2} align="center">
                  <Text fontSize="sm" fontWeight="medium">
                    {processJob.message || 'Processando documentação...'}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {Math.round(processJob.progress || 0)}%
                  </Text>
                </HStack>
                <Progress
                  value={processJob.progress || 0}
                  size="sm"
                  colorScheme={processJob.status === 'failed' ? 'red' : 'teal'}
                  borderRadius="sm"
                  hasStripe={processing}
                  isAnimated={processing}
                />
                {(processJob.total_chunks || 0) > 0 && (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Parte {processJob.current_chunk || 0} de {processJob.total_chunks}
                  </Text>
                )}
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>

      <Card ref={manualCardRef}>
        <CardHeader>
          <Heading size="md">{editingId ? 'Editar Integração' : 'Cadastrar Integração Manualmente'}</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            {editingId
              ? 'Altere nome, URL, autenticação, endpoint e os mapeamentos. Deixe o segredo em branco para manter o atual. Se houver várias rotas, escolha qual está editando e salve antes de trocar — as demais permanecem.'
              : 'Configure o endpoint externo, mapeie os campos enviados e recebidos, e indique quais dados do sistema alimentam cada requisição.'}
          </Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Nome da integração</FormLabel>
                <Input
                  value={manualForm.name}
                  onChange={(e) => updateManualForm('name', e.target.value)}
                  placeholder="Consulta DETRAN / API Municipal"
                  size="sm"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">URL Base</FormLabel>
                <Input
                  value={manualForm.base_url}
                  onChange={(e) => updateManualForm('base_url', e.target.value)}
                  placeholder="https://api.exemplo.gov.br/v1"
                  size="sm"
                />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel fontSize="sm">Descrição</FormLabel>
              <Input
                value={manualForm.description}
                onChange={(e) => updateManualForm('description', e.target.value)}
                placeholder="Uso previsto, ambiente, observações do contrato..."
                size="sm"
              />
            </FormControl>

            {editingRoutes.length > 1 && (
              <FormControl>
                <FormLabel fontSize="sm">Rota em edição</FormLabel>
                <Select
                  value={editingRouteIndex}
                  onChange={(e) => loadEditingRoute(Number(e.target.value))}
                  size="sm"
                >
                  {editingRoutes.map((route, index) => (
                    <option key={`${route.method || 'ROTA'}-${route.path || index}`} value={index}>
                      {route.name || `${route.method || ''} ${route.path || route.full_url || `Rota ${index + 1}`}`}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Função no sistema</FormLabel>
                <Select
                  value={manualForm.route_purpose}
                  onChange={(e) => handleManualPurposeChange(e.target.value)}
                  size="sm"
                >
                  {(manualSchema?.purposes || [
                    { value: 'register_infraction', label: 'Enviar/registrar infração' },
                    { value: 'query_vehicle', label: 'Consultar veículo' },
                    { value: 'query_driver', label: 'Consultar condutor' },
                    { value: 'query_infraction', label: 'Consultar infração' },
                    { value: 'register_passage', label: 'Enviar passagem' },
                    { value: 'other', label: 'Outro' }
                  ]).map((purpose) => (
                    <option key={purpose.value} value={purpose.value}>{purpose.label}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm">Método</FormLabel>
                <Select
                  value={manualForm.route_method}
                  onChange={(e) => updateManualForm('route_method', e.target.value)}
                  size="sm"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </Select>
              </FormControl>

              <FormControl gridColumn={{ base: 'auto', md: 'span 2' }} isRequired>
                <FormLabel fontSize="sm">Endpoint</FormLabel>
                <Input
                  value={manualForm.route_path}
                  onChange={(e) => updateManualForm('route_path', e.target.value)}
                  placeholder="/veiculos/{placa} ou /veiculos"
                  size="sm"
                />
                <FormHelperText fontSize="xs">
                  Use chaves para parâmetros de rota, como {'{placa}'}. Em GET sem chaves, o mapeamento vira query string.
                </FormHelperText>
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm">Nome da rota</FormLabel>
                <Input
                  value={manualForm.route_name}
                  onChange={(e) => updateManualForm('route_name', e.target.value)}
                  placeholder="Consulta veículo por placa"
                  size="sm"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Autenticação</FormLabel>
                <Select
                  value={manualForm.auth_type}
                  onChange={(e) => updateManualForm('auth_type', e.target.value)}
                  size="sm"
                >
                  <option value="none">Sem autenticação</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="authorization">Authorization (valor exato)</option>
                  <option value="api_key">API Key</option>
                  <option value="basic">Basic Auth</option>
                </Select>
              </FormControl>
            </SimpleGrid>

            {(manualForm.auth_type === 'bearer' || manualForm.auth_type === 'authorization') && (
              <FormControl>
                <FormLabel fontSize="sm">
                  {manualForm.auth_type === 'authorization' ? 'Valor do header Authorization' : 'Token Bearer'}
                </FormLabel>
                <Input
                  type="password"
                  value={manualForm.auth_token}
                  onChange={(e) => updateManualForm('auth_token', e.target.value)}
                  placeholder={manualForm.auth_type === 'authorization'
                    ? 'SharedAccessSignature sr=...&sig=...&se=...&skn=...'
                    : (editingId ? 'Em branco mantém o token atual' : 'Token enviado como Authorization: Bearer')}
                  size="sm"
                />
                {manualForm.auth_type === 'authorization' && (
                  <FormHelperText fontSize="xs">
                    Enviado exatamente como está, sem o prefixo Bearer. Use para SharedAccessSignature do Service Bus.
                  </FormHelperText>
                )}
              </FormControl>
            )}

            {manualForm.auth_type === 'api_key' && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Header da API Key</FormLabel>
                  <Input
                    value={manualForm.auth_api_header}
                    onChange={(e) => updateManualForm('auth_api_header', e.target.value)}
                    placeholder="X-API-Key"
                    size="sm"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">API Key</FormLabel>
                  <Input
                    type="password"
                    value={manualForm.auth_api_key}
                    onChange={(e) => updateManualForm('auth_api_key', e.target.value)}
                    placeholder={editingId ? 'Em branco mantém a chave atual' : 'Chave da API externa'}
                    size="sm"
                  />
                </FormControl>
              </SimpleGrid>
            )}

            {manualForm.auth_type === 'basic' && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel fontSize="sm">Usuário</FormLabel>
                  <Input
                    value={manualForm.auth_username}
                    onChange={(e) => updateManualForm('auth_username', e.target.value)}
                    placeholder="username"
                    size="sm"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Senha</FormLabel>
                  <Input
                    type="password"
                    value={manualForm.auth_password}
                    onChange={(e) => updateManualForm('auth_password', e.target.value)}
                    placeholder={editingId ? 'Em branco mantém a senha atual' : 'password'}
                    size="sm"
                  />
                </FormControl>
              </SimpleGrid>
            )}

            <FormControl>
              <FormLabel fontSize="sm">Mapeamento da requisição (body / query / path)</FormLabel>
              <Text fontSize="xs" color="gray.500" mb={2}>
                À esquerda, o nome do campo que a API externa recebe. Use ponto para objeto aninhado, como camera.numero.
                Em Modelo, escreva o texto e encaixe campos do sistema entre chaves, como {'{camera_code}-1'}.
              </Text>
              <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                <SimpleGrid
                  columns={{ base: 1, md: 4 }}
                  templateColumns={{ md: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr) 40px' }}
                  spacing={0}
                  bg="gray.50"
                  px={3}
                  py={2}
                >
                  <Text fontSize="xs" fontWeight="bold" color="gray.600">Campo na API</Text>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600">Origem</Text>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600">Valor</Text>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" textAlign="center" />
                </SimpleGrid>
                <VStack spacing={0} align="stretch">
                  {manualRequestRows.map((row) => (
                    <SimpleGrid
                      key={row.id}
                      columns={{ base: 1, md: 4 }}
                      templateColumns={{ md: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr) 40px' }}
                      spacing={2}
                      px={3}
                      py={2}
                      borderTop="1px solid"
                      borderColor="gray.100"
                      alignItems="center"
                    >
                      <Input
                        size="sm"
                        value={row.apiField}
                        onChange={(e) => updateManualRequestRow(row.id, 'apiField', e.target.value)}
                        placeholder="camera.numero"
                      />
                      <Select
                        size="sm"
                        value={row.sourceType}
                        onChange={(e) => updateManualRequestRow(row.id, 'sourceType', e.target.value)}
                      >
                        <option value="field">Campo do sistema</option>
                        <option value="template">Modelo</option>
                        <option value="static">Valor fixo</option>
                      </Select>
                      {row.sourceType === 'field' ? (
                        <Select
                          size="sm"
                          value={row.value}
                          onChange={(e) => updateManualRequestRow(row.id, 'value', e.target.value)}
                          placeholder="Selecione o campo interno"
                        >
                          <option value="">Selecione...</option>
                          {(manualSchema?.local_fields?.[manualForm.route_purpose] || manualSchema?.system_fields || []).map((field) => (
                            <option key={field.key} value={field.key}>{field.key} - {field.label}</option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          size="sm"
                          value={row.value}
                          onChange={(e) => updateManualRequestRow(row.id, 'value', e.target.value)}
                          placeholder={row.sourceType === 'template' ? '{camera_code}-1' : 'valor fixo'}
                        />
                      )}
                      <IconButton
                        aria-label="Remover campo"
                        icon={<CloseIcon boxSize={2.5} />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        justifySelf="center"
                        onClick={() => removeManualRequestRow(row.id)}
                      />
                    </SimpleGrid>
                  ))}
                </VStack>
              </Box>
              <Button
                leftIcon={<AddIcon />}
                size="xs"
                variant="outline"
                mt={2}
                onClick={addManualRequestRow}
              >
                Adicionar campo da requisição
              </Button>
              <FormHelperText fontSize="xs">
                Exemplo: campo_api ← plate, outro_campo ← valor fixo.
              </FormHelperText>
            </FormControl>

            {(manualSchema?.local_fields?.[manualForm.route_purpose] || manualSchema?.system_fields || []).length > 0 && (
              <Box bg="blue.50" border="1px solid" borderColor="blue.100" borderRadius="md" p={3}>
                <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={2}>
                  Campos do sistema disponíveis para esta função
                </Text>
                <Flex gap={2} wrap="wrap">
                  {(manualSchema?.local_fields?.[manualForm.route_purpose] || manualSchema?.system_fields || []).map((field) => (
                    <Tag
                      key={field.key}
                      size="sm"
                      colorScheme="blue"
                      cursor="pointer"
                      onClick={() => applySystemFieldToRequestRow(field.key)}
                    >
                      <TagLabel>{field.key} - {field.label}</TagLabel>
                    </Tag>
                  ))}
                </Flex>
                <Text fontSize="xs" color="blue.600" mt={2}>
                  Clique em um campo para adicioná-lo rapidamente ao mapeamento da requisição.
                </Text>
              </Box>
            )}

            <FormControl>
              <FormLabel fontSize="sm">Mapeamento da resposta</FormLabel>
              <Text fontSize="xs" color="gray.500" mb={2}>
                Defina quais campos da resposta da API externa serão guardados no sistema. Use caminhos com ponto, como data.veiculo.placa.
              </Text>
              <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                <SimpleGrid
                  columns={{ base: 1, md: 3 }}
                  templateColumns={{ md: 'minmax(0, 1fr) minmax(0, 2fr) 40px' }}
                  spacing={0}
                  bg="gray.50"
                  px={3}
                  py={2}
                >
                  <Text fontSize="xs" fontWeight="bold" color="gray.600">Campo no sistema</Text>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600">Caminho na resposta da API</Text>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" textAlign="center" />
                </SimpleGrid>
                <VStack spacing={0} align="stretch">
                  {manualResponseRows.map((row) => (
                    <SimpleGrid
                      key={row.id}
                      columns={{ base: 1, md: 3 }}
                      templateColumns={{ md: 'minmax(0, 1fr) minmax(0, 2fr) 40px' }}
                      spacing={2}
                      px={3}
                      py={2}
                      borderTop="1px solid"
                      borderColor="gray.100"
                      alignItems="center"
                    >
                      <Input
                        size="sm"
                        value={row.internalField}
                        onChange={(e) => updateManualResponseRow(row.id, 'internalField', e.target.value)}
                        placeholder="plate, renavam, restrictions..."
                        list={`response-field-suggestions-${manualForm.route_purpose}`}
                      />
                      <Input
                        size="sm"
                        value={row.apiPath}
                        onChange={(e) => updateManualResponseRow(row.id, 'apiPath', e.target.value)}
                        placeholder="data.campo ou resultado.item.valor"
                      />
                      <IconButton
                        aria-label="Remover mapeamento de resposta"
                        icon={<CloseIcon boxSize={2.5} />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        justifySelf="center"
                        onClick={() => removeManualResponseRow(row.id)}
                      />
                    </SimpleGrid>
                  ))}
                </VStack>
              </Box>
              <datalist id={`response-field-suggestions-${manualForm.route_purpose}`}>
                {(manualSchema?.response_field_suggestions?.[manualForm.route_purpose] || []).map((field) => (
                  <option key={field.key} value={field.key}>{field.label}</option>
                ))}
              </datalist>
              <Button
                leftIcon={<AddIcon />}
                size="xs"
                variant="outline"
                mt={2}
                onClick={addManualResponseRow}
              >
                Adicionar campo da resposta
              </Button>
              {(manualSchema?.response_field_suggestions?.[manualForm.route_purpose] || []).length > 0 && (
                <Box mt={3}>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={2}>Sugestões de campos de destino</Text>
                  <Flex gap={2} wrap="wrap">
                    {(manualSchema?.response_field_suggestions?.[manualForm.route_purpose] || []).map((field) => (
                      <Tag
                        key={field.key}
                        size="sm"
                        colorScheme="purple"
                        cursor="pointer"
                        onClick={() => applyResponseSuggestionToRow(
                          field.key,
                          manualSchema?.response_mapping_examples?.[manualForm.route_purpose]?.[field.key]
                        )}
                      >
                        <TagLabel>{field.key} - {field.label}</TagLabel>
                      </Tag>
                    ))}
                  </Flex>
                </Box>
              )}
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Dados para testar a rota</FormLabel>
              <Textarea
                value={manualTestPayloadText}
                onChange={(e) => setManualTestPayloadText(e.target.value)}
                minH="130px"
                size="sm"
                fontFamily="mono"
                fontSize="xs"
                placeholder='{"plate":"ABC1D23"}'
              />
              <FormHelperText fontSize="xs">
                Informe o JSON de entrada que o sistema usará para montar path params, query string e body antes de salvar a integração.
              </FormHelperText>
            </FormControl>

            <Button
              variant="outline"
              colorScheme="teal"
              onClick={handleTestManualRoute}
              isLoading={testingManual}
              loadingText="Testando..."
            >
              Testar Rota Antes de Salvar
            </Button>

            {manualTestResult && (
              <Box
                bg={manualTestResult.success ? 'green.50' : 'red.50'}
                border="1px solid"
                borderColor={manualTestResult.success ? 'green.200' : 'red.200'}
                borderRadius="md"
                p={3}
              >
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="bold" color={manualTestResult.success ? 'green.700' : 'red.700'}>
                    {manualTestResult.success ? 'Teste concluído com sucesso' : 'Falha no teste da rota'}
                  </Text>
                  {manualTestResult.response?.status && (
                    <Badge colorScheme={manualTestResult.success ? 'green' : 'red'}>
                      HTTP {manualTestResult.response.status}
                    </Badge>
                  )}
                </HStack>

                <VStack spacing={3} align="stretch">
                  {manualTestResult.message && (
                    <Text fontSize="xs" color={manualTestResult.success ? 'green.700' : 'red.700'}>
                      {manualTestResult.message}
                    </Text>
                  )}

                  {manualTestResult.request && (
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>Request gerado</Text>
                      <Code fontSize="xs" display="block" whiteSpace="pre-wrap" p={2}>
                        {JSON.stringify(manualTestResult.request, null, 2)}
                      </Code>
                    </Box>
                  )}

                  {(manualTestResult.response?.data || manualTestResult.external_error) && (
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                        {manualTestResult.success ? 'Resposta bruta da API' : 'Erro retornado pela API'}
                      </Text>
                      <Code fontSize="xs" display="block" whiteSpace="pre-wrap" p={2}>
                        {JSON.stringify(manualTestResult.response?.data || manualTestResult.external_error, null, 2)}
                      </Code>
                    </Box>
                  )}

                  {manualTestResult.response?.mapped && (
                    <Box>
                      <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>Resposta mapeada para o sistema</Text>
                      <Code fontSize="xs" display="block" whiteSpace="pre-wrap" p={2}>
                        {JSON.stringify(manualTestResult.response.mapped, null, 2)}
                      </Code>
                    </Box>
                  )}
                </VStack>
              </Box>
            )}

            {editingId ? (
              <HStack>
                <Button variant="ghost" onClick={cancelEdit}>
                  Cancelar
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSaveEdit}
                  isLoading={savingManual}
                  loadingText="Salvando..."
                  leftIcon={<CheckIcon />}
                >
                  Salvar alterações
                </Button>
              </HStack>
            ) : (
              <Button
                colorScheme="blue"
                onClick={handleCreateManual}
                isLoading={savingManual}
                loadingText="Salvando..."
                leftIcon={<CheckIcon />}
              >
                Criar Integração Manual
              </Button>
            )}
          </VStack>
        </CardBody>
      </Card>

      <Divider />

      {/* Lista de integrações existentes */}
      <Box>
        <HStack justify="space-between" mb={4}>
          <Heading size="md">Integrações Configuradas</Heading>
          <IconButton
            icon={<RepeatIcon />}
            size="sm"
            onClick={fetchIntegrations}
            aria-label="Recarregar"
          />
        </HStack>

        {loading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" />
          </Flex>
        ) : integrations.length === 0 ? (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">Nenhuma integração configurada. Use o formulário acima para criar uma.</Text>
          </Alert>
        ) : (
          <VStack spacing={4} align="stretch">
            {integrations.map((integration) => {
              const routes = parseRoutes(integration.routes);
              const manualIntegration = isManualIntegration(integration);
              const hasRegisterRoute = routes.some(r => r.purpose === 'register_infraction');
              const hasVehicleQuery = routes.some(r => r.purpose === 'query_vehicle');
              const hasDriverQuery = routes.some(r => r.purpose === 'query_driver');
              const hasInfractionQuery = routes.some(r => r.purpose === 'query_infraction');

              return (
                <Card key={integration.id} variant="outline">
                  <CardHeader pb={2}>
                    <Flex justify="space-between" align="center">
                      <HStack>
                        <Heading size="sm">{integration.name}</Heading>
                        <Badge colorScheme={integration.is_active ? 'green' : 'gray'}>
                          {integration.is_active ? 'Ativa' : 'Inativa'}
                        </Badge>
                        {hasRegisterRoute && (
                          <Badge colorScheme="purple">Envio de Infrações</Badge>
                        )}
                        {hasVehicleQuery && (
                          <Badge colorScheme="cyan">Consulta Veículo</Badge>
                        )}
                        {hasDriverQuery && (
                          <Badge colorScheme="orange">Consulta Condutor</Badge>
                        )}
                        {hasInfractionQuery && (
                          <Badge colorScheme="pink">Consulta Infração</Badge>
                        )}
                        <Badge colorScheme={manualIntegration ? 'yellow' : 'blue'}>{getSourceLabel(integration)}</Badge>
                      </HStack>
                      <HStack>
                        <Switch
                          isChecked={integration.is_active}
                          onChange={() => handleToggle(integration.id)}
                          size="sm"
                        />
                        {!manualIntegration && (
                          <IconButton
                            icon={<RepeatIcon />}
                            size="xs"
                            onClick={() => handleReprocess(integration.id)}
                            aria-label="Reprocessar"
                            title="Reprocessar documentação com IA"
                          />
                        )}
                        <IconButton
                          icon={<DeleteIcon />}
                          size="xs"
                          colorScheme="red"
                          variant="ghost"
                          onClick={() => handleDelete(integration.id)}
                          aria-label="Excluir"
                        />
                      </HStack>
                    </Flex>
                    {integration.description && (
                      <Text fontSize="xs" color="gray.500" mt={1}>{integration.description}</Text>
                    )}
                  </CardHeader>

                  <CardBody pt={0}>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
                      <Box>
                        <Text fontSize="xs" color="gray.500">URL Base</Text>
                        <Code fontSize="xs">{integration.base_url || 'Não definida'}</Code>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">Autenticação</Text>
                        <Text fontSize="sm">{integration.auth_type || 'Nenhuma'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">Rotas Identificadas</Text>
                        <Text fontSize="sm" fontWeight="bold">{routes.length}</Text>
                      </Box>
                    </SimpleGrid>

                    <Button size="xs" colorScheme="blue" variant="outline" onClick={() => openEdit(integration)} mb={3}>
                      Editar integração
                    </Button>

                    <IntegrationLogs integrationId={integration.id} />

                    <Accordion allowToggle>
                      <AccordionItem border="none">
                        <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                          <Text fontSize="sm" fontWeight="medium" flex={1} textAlign="left">
                            📋 Ver Rotas ({routes.length})
                          </Text>
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel px={0}>
                          <VStack spacing={2} align="stretch">
                            {routes.map((route, idx) => (
                              <Box key={idx} p={2} bg="gray.50" borderRadius="md" borderLeft="3px solid" borderLeftColor={`${getMethodColor(route.method)}.400`}>
                                <HStack mb={1}>
                                  <Badge colorScheme={getMethodColor(route.method)} fontSize="xs">
                                    {route.method}
                                  </Badge>
                                  <Code fontSize="xs">{route.path || route.full_url}</Code>
                                  <Tag size="sm" colorScheme="teal">
                                    <TagLabel fontSize="xs">{getPurposeLabel(route.purpose)}</TagLabel>
                                  </Tag>
                                </HStack>
                                <Text fontSize="xs" color="gray.600">{route.description}</Text>
                                {route.request_body && Object.keys(route.request_body).length > 0 && (
                                  <Box mt={1}>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.500">Body:</Text>
                                    <Code fontSize="xs" display="block" whiteSpace="pre-wrap" p={1}>
                                      {JSON.stringify(route.request_body, null, 2)}
                                    </Code>
                                  </Box>
                                )}
                                {route.field_mappings && Object.keys(route.field_mappings).length > 0 && (
                                  <Box mt={1}>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.500">Mapeamento da requisição:</Text>
                                    <Code fontSize="xs" display="block" whiteSpace="pre-wrap" p={1}>
                                      {JSON.stringify(route.field_mappings, null, 2)}
                                    </Code>
                                  </Box>
                                )}
                                {route.response_mappings && Object.keys(route.response_mappings).length > 0 && (
                                  <Box mt={1}>
                                    <Text fontSize="xs" fontWeight="bold" color="gray.500">Mapeamento da resposta:</Text>
                                    <Code fontSize="xs" display="block" whiteSpace="pre-wrap" p={1}>
                                      {JSON.stringify(route.response_mappings, null, 2)}
                                    </Code>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </CardBody>
                </Card>
              );
            })}
          </VStack>
        )}
      </Box>
    </VStack>
  );
};

export default ApiIntegrationTab;
